import {
  SpeakerColorEntry,
  SPEAKER_COLORS,
  formatVoiceLabel,
  getSpeakerKey,
  isDisplayableDiarization,
  isNumeric,
} from '@app/modules/diarization/speaker-utils';
import { DiarizationType, WpWordsType } from '@app/modules/search/types';
import { toDateTimeStr, toTimestamp } from '@mi-front/core';
import { Row } from '@tanstack/react-table';
import { lighten } from 'polished';
import { FormattedMessage } from 'react-intl';
import {
  FormattedTranscriptionType,
  TranscriptionResponse,
  TranscriptionType,
} from './transcriptions.types';

/** Re-export for YouTube stats and other consumers of the shared pastel palette. */
export const pastelColors = SPEAKER_COLORS;

export { formatVoiceLabel, isNumeric };

export const getConfidenceColor = (confidence: number) => {
  if (confidence > 0.75) {
    return lighten(0.03, '#dbe9db');
  }
  if (confidence > 0.5) {
    return lighten(0.03, '#faf1d1');
  }
  if (confidence > 0) {
    return lighten(0.03, '#ffbeae');
  }
  return 'transparent';
};

export const getConfidenceStyle = (
  confidence: number,
): { backgroundColor: string; color?: string } => {
  const backgroundColor = getConfidenceColor(confidence);
  return confidence > 0 ? { backgroundColor, color: '#333333' } : { backgroundColor };
};

const getDiarizationColor = (text: WpWordsType, diarizations: (DiarizationType | null)[]) => {
  const color = diarizations.find((d) => {
    return Number(d?.segment.start) <= text.start && Number(d?.segment.end) > text.end;
  })?.color;

  return color ? lighten(0.15, color) : 'transparent';
};

export const parseTranscription = (data: TranscriptionResponse[]): TranscriptionType[] => {
  const array = data
    .map((item) => {
      if (item[0].tags) {
        return {
          diarization: item[0].diarization,
          elastic_data: item[0].elastic_data,
          highlight: item[0].highlight,
          length: item[0].length,
          tags: item[0].tags,
          time: item[0].time,
          wp_words: item[0].wp_words,
        };
      }
      return null;
    })
    .filter((data) => data);
  // @ts-expect-error filter removes nulls but TS can't infer that
  return array;
};

/**
 * Formats diarization entries for a transcription row, attaching speaker color and person_id.
 */
const formatDiarization = (
  item: TranscriptionType,
  diarization: DiarizationType[],
  speakers?: SpeakerColorEntry[],
) => {
  return Array.isArray(diarization)
    ? diarization
        .map((d) => {
          return isDisplayableDiarization(d)
            ? {
                time: toDateTimeStr(toTimestamp(item.time) + d.segment.start),
                distance: d.distance,
                label: d.label,
                segment: d.segment,
                voice_label: d.voice_label,
                person_id: d.person_id,
                color: speakers?.find((s) => s.key === getSpeakerKey(d))?.color,
              }
            : null;
        })
        .flat()
        .filter((data) => data !== null)
    : [];
};

/** Builds word hover ranges (absolute ms) for video-mode hovered diarization overlay. */
export const formatFragmentsHover = (data: TranscriptionType[]) => {
  return data
    ? data
        .map((transcription) => {
          return transcription.wp_words.map((word) => {
            return {
              start: toTimestamp(transcription.time) + word.start * 1000,
              end: toTimestamp(transcription.time) + word.end * 1000,
              word: word.word,
            };
          });
        })
        .flat()
    : [];
};

export const formatTranscript = (
  data: TranscriptionType[],
  speakers?: SpeakerColorEntry[],
): FormattedTranscriptionType[] => {
  const formattedText = data?.map((item) => {
    const diarizations = item?.diarization
      ? formatDiarization(item, item?.diarization, speakers)
      : [];
    return {
      elastic_data: {
        content: item.elastic_data.content,
        customId: item.elastic_data.customId,
        diarization: diarizations,
        file: item.elastic_data.file,
        raw_content:
          !item.tags || item.tags === 'speech' ? (
            item.elastic_data.raw_content
          ) : (
            <FormattedMessage
              id="audit.transcriptionScreen.music"
              defaultMessage="[Música/Silencio]"
            />
          ),
        slug: item.elastic_data.slug,
        tags: item.elastic_data.tags,
        time: item.elastic_data.time,
        words: item.elastic_data.words,
        wp_content: item.elastic_data.wp_content,
        wp_words: item.elastic_data.wp_words,
      },
      highlight: item.highlight,
      length: item.length,
      tags: item.tags,
      time: item.time,
      wp_words:
        !item.tags || item.tags === 'speech' ? (
          item.wp_words.map((text, index) => {
            return (
              <span style={getConfidenceStyle(text.confidence)} key={`text-wp-confidence-${index}`}>
                {text.word}
              </span>
            );
          })
        ) : (
          <FormattedMessage
            id="audit.transcriptionScreen.music"
            defaultMessage="[Música/Silencio]"
          />
        ),
      diarization_words:
        !item.tags || item.tags === 'speech' ? (
          item.wp_words.map((text, index) => {
            return (
              <span
                style={{
                  backgroundColor: getDiarizationColor(text, diarizations),
                }}
                key={`text-wp-diarization-${index}`}
              >
                {text.word}
              </span>
            );
          })
        ) : (
          <FormattedMessage
            id="audit.transcriptionScreen.music"
            defaultMessage="[Música/Silencio]"
          />
        ),
      original_words: item.wp_words,
    };
  });
  return formattedText;
};

export const filteredTranscript = (
  row: Row<FormattedTranscriptionType>,
  raw_content: string,
  filterValue: string,
) => {
  const value = row.getValue(raw_content);
  if (row.original.tags === 'speech') {
    if (typeof value === 'string') {
      return value.toLowerCase().includes(filterValue.toLowerCase());
    } else {
      const match = (value as JSX.Element[]).find((element: JSX.Element) => {
        const word = element.props.children.toLowerCase().trim();
        return word.includes(filterValue.toLowerCase());
      });
      return !!match;
    }
  }
  return false;
};
